/**
 * @Date: 2024-08-16 14:48:39
 * @Component: PagingSelectGroup
 * @Description: 分页下拉选择器组件，支持虚拟滚动、分组展示、海量数据处理
 *
 * ## 功能特性
 * - 支持无限滚动分页加载（virtual scrolling）
 * - 支持分组模式（grouped=true）展示层级化数据
 * - 支持扁平模式（grouped=false）处理普通列表
 * - 支持搜索、过滤、多选功能
 * - 支持固定项（pinnedOptions）始终显示在最上方
 * - 完整的数据验证和错误处理
 *
 * ## 使用场景
 * 1. **大数据集选择**: 数据量 > 1000 时，虚拟滚动保证性能
 * 2. **分组管理**: 如推送通知中的【配置分组 -> 推送组】两层结构
 * 3. **实时过滤**: 支持搜索、分页动态加载
 *
 * ## 核心概念
 * - **GroupOption**: 分组数据结构 {groupLabel, groupKey, children}
 * - **VirtualizedOptions**: 虚拟滚动配置，包含外层滚动容器的 ref
 * - **ExpandedGroupIds**: 当前展开的分组 ID 数组，用于展开/收起分组
 * - **Scroll Pagination**: 滚动到底部时自动加载下一页
 *
 * ## 性能优化
 * - 使用 useRef 避免闭包陷阱，scroll 事件始终看到最新状态
 * - 虚拟滚动（Tea-component Tree）只渲染可见区域
 * - 搜索防抖，避免频繁 API 调用
 * - 分组自动展开策略，减少用户操作
 *
 * @example
 * // 分组模式示例
 * <PagingSelectGroup
 *   grouped={true}
 *   defaultGroupOptions={[
 *     {
 *       groupKey: "config1",
 *       groupLabel: "[配置 A]",
 *       children: [
 *         { value: "group1", text: "推送组 1" },
 *         { value: "group2", text: "推送组 2" }
 *       ]
 *     }
 *   ]}
 *   getFilterGroupOptions={(list) => transformToGroupOptions(list)}
 *   onChange={(val) => console.log(val)}
 * />
 */

import { intlGet } from '@/locales';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CheckboxGroupChangeContext } from 'tdesign-react';
import { Checkbox, List, Loading, Popup, Tree } from 'tdesign-react';
// components
import { Link, SearchBox } from '@/components/tdesign';
import CustomSelect from './customSelect';
import CustomTree from './scrollList';
// api
import { handleReturnResults } from '@/api/interfaceUtils';
// type
import type { PageInfoIF } from '@/api/data.d';
import type { SelectPopupProps } from '@/components/tdesign/data.d';
import { InputSizeEnum } from '@/components/tdesign/data.d';
import type { StatusTipType } from '@/definition/common/data.d';
import { StatusTipEnum } from '@/definition/common/data.d';
import type { optsTypes, SelectBseProps } from './utils/typings';
import { DisplayTextEnum } from './utils/typings';
// utils
import { throttle } from '@/utils/common';
import {
  isArray,
  isEmptyObject,
  isNumber,
  isString,
} from '@/utils/common/dataJudgment';
import { ALL } from './utils/config';
import { checkValue, selectTreeChildren } from './utils/functions';

/** Group option type */
export type GroupOption = {
  /** Group label displayed as header */
  groupLabel: string;
  /** Unique key for the group */
  groupKey: string;
  /** Options within this group */
  children: optsTypes[];
};

type propsType = {
  /** Display type */
  renderType: DisplayTextEnum;
  /** Button display label */
  label?: string;
  /** Search placeholder */
  searchPlaceHolder?: string;
  /** Default dropdown options */
  defaultOptions?: any[];
  /** Empty text */
  emptyText?: string | ReactNode;
  /** Whether to auto request - default false */
  isAutoRequest?: boolean;
  /** list - level key, default dataList */
  listKey?: string;
  /** data - level key, default empty */
  dataKey?: string;
  /** Default search params */
  defaultSearchParams?: any;
  /** Search key */
  searchKey?: string;
  /** dropDown props */
  // dropDownProps?: Omit<DropdownProps, any>;
  /** Whether to stage operation results */
  staging?: boolean;
  /** Status */
  status?: StatusTipType;
  /** Full backup list */
  fullBackupList?: any[];
  /** Whether to enable group mode */
  grouped?: boolean;
  /** Default group options (used when grouped=true) */
  defaultGroupOptions?: GroupOption[];
  /** Key in API response for extracting group info, default 'group' */
  groupFieldKey?: string;
  /** Whether to enable multiple selection - default true */
  isMultiple?: boolean;
  /** Pinned options displayed at the top of the dropdown, outside of any group (used when grouped=true) */
  pinnedOptions?: optsTypes[];
  overlayInnerStyle?: SelectPopupProps['overlayInnerStyle'];
  onSearch?: (val: any) => void;
  /** Footer content */
  footer?: (params: { selectValue: any; close: () => void }) => React.ReactNode;
  /** Clear */
  onClear?: (val: any) => any;
  /** Change event */
  onChange: (val: any, config: any) => any;
  /** Custom render */
  customRender?: ({
    selectValue,
    selectChildValue,
    selectChildList,
    list,
  }: {
    selectValue: string[];
    selectChildValue?: any[];
    selectChildList?: any[];
    list: optsTypes[];
  }) => ReactNode;
  /** Get list API */
  getList?: (data: any) => Promise<any>;
  /** Filter list method */
  getFilterOptions?: (
    list: any[],
    pageInfo: PageInfoIF,
    isAllList: boolean,
  ) => any[];
  /** Custom list item disable */
  setListDisable?: (list: any, selectValue: any) => any[];
  /** Filter group options method (used when grouped=true, transforms API data into GroupOption[]) */
  getFilterGroupOptions?: (
    list: any[],
    pageInfo: PageInfoIF,
    isAllList: boolean,
  ) => GroupOption[];
  /**
   * 自定义从响应体中提取总条数的方法（可选，兜底方案）
   *
   * 默认读取：`res.data[dataKey]?.total` 或 `res.data?.total`
   *
   * 当后端把 total 嵌套在其它字段里（例如 `res.data.pageInfo.total`、
   * `res.data.page_info.total` 等），且全局拦截器未将其平铺到约定位置时，
   * 可通过该 Props 指定如何取值，避免滚动分页因拿不到 total 而失效。
   *
   * @example
   * getTotal={(res) => res?.data?.pageInfo?.total}
   */
  getTotal?: (res: any) => number | undefined;
} & SelectBseProps;

/**
 * 将分组数据展平成一维数组
 *
 * @param groups - 分组数据数组，每个分组包含 children 选项数组
 * @returns 展平后的选项数组（所有分组的 children 合并）
 *
 * @example
 * const groups = [
 *   { groupKey: "g1", groupLabel: "Group 1", children: [{value: "a", text: "A"}] },
 *   { groupKey: "g2", groupLabel: "Group 2", children: [{value: "b", text: "B"}] }
 * ];
 * const flat = flattenGroupOptions(groups);
 * // Result: [{value: "a", text: "A"}, {value: "b", text: "B"}]
 */
const flattenGroupOptions = (groups: GroupOption[]): optsTypes[] => {
  return groups.reduce<optsTypes[]>((acc, group) => {
    return [...acc, ...(group.children || [])];
  }, []);
};

const PagingSelectGroup = (props: propsType, refs: any) => {
  const {
    renderType = DisplayTextEnum.CUSTOM_TEXT,
    disabled,
    clearable,
    searchable,
    value,
    label,
    placeholder,
    defaultValue = [],
    searchPlaceHolder = '请输入',
    defaultOptions = [],
    emptyText = '暂无数据',
    isAutoRequest = false,
    listKey = 'list',
    dataKey,
    defaultSearchParams,
    searchKey = 'name',
    footer,
    allOption,
    // dropDownProps = {},
    staging = false,
    status,
    fullBackupList,
    grouped = false,
    defaultGroupOptions = [],
    groupFieldKey = 'group',
    isMultiple = true,
    pinnedOptions = [],
    overlayInnerStyle,
    onSearch,
    customRender,
    onClear,
    onChange,
    getList,
    getFilterOptions,
    getFilterGroupOptions,
    getTotal,
    setListDisable,
  } = props;

  // ============ State Management ============

  /** 下拉框是否展开 */
  const [open, setOpen] = useState<boolean>(false);
  const onClosePop = () => setOpen(false);

  /** 搜索和分页参数 {pageIndex, pageSize, searchKey, ...} */
  const [searchParams, setSearchParams] = useState<any>({
    pageIndex: 1,
    pageSize: 10,
    ...defaultSearchParams,
  });

  /**
   * 更新搜索参数
   *
   * @param params - 新增或更新的参数对象，会与现有参数合并
   * @example setSearchValue({ keyword: "test" }) // pageIndex 从 1 重置
   */
  const setSearchValue = (params: any) => {
    const data = {
      ...searchParams,
      ...params,
    };
    setSearchParams(data);
  };

  /**
   * 归一化值为数组格式
   * 内部始终以数组存储，便于 Tree 组件处理多选
   *
   * @param val - 可能是单值、数组或空值
   * @returns 永远返回数组格式
   */
  const normalizeToArray = (val: any): any[] => {
    if (!val) return [];
    if (isArray(val)) return val;
    return [val];
  };

  /** 当前选中的值（内部始终存储为数组） */
  const [selectValue, setSelectValue] = useState<any>(normalizeToArray(value));

  /**
   * 重置选中值到默认状态
   * 触发 onChange 和 onClear 回调
   */
  const resetSelectValue = () => {
    const resetVal = !isMultiple ? '' : defaultValue;
    setSelectValue(!isMultiple ? [] : defaultValue);
    onChange(resetVal, { option: {} });
    onClear?.(resetVal);
  };

  /** 默认选项列表 */
  const [defaultOpts, setDefaultOpts] = useState<optsTypes[]>(defaultOptions);
  /** 当前下拉框显示的列表 */
  const [dataList, setDataList] = useState<any>([...defaultOpts]);
  /** 完整列表备份（用于搜索还原） */
  const [optsCopy, setOptsCopy] = useState<any>([...defaultOpts]);
  /** 请求状态（loading/success/error） */
  const [reqStatus, setReqStatus] = useState<StatusTipType>(StatusTipEnum.init);

  /**
   * 加载中标志 (useRef 而非 useState)
   * 原因: 避免闭包陷阱。scroll 事件回调无法获取最新的加载状态
   * 使用 ref 保证事件处理时始终看到最新值
   */
  const isLoadingRef = useRef(false);

  /** 是否已触发搜索/请求数据 */
  const [isSearch, setIsSearch] = useState<boolean>(isAutoRequest);

  /**
   * 分页配置 {total, pageIndex, pageSize}
   * total: API 返回的总数据条数，用于判断是否需要继续加载
   */
  const [pageConfig, setPageConfig] = useState<any>({
    total: defaultOptions.length || 0,
  });

  // ============ Grouped Mode State ============

  /** 分组数据列表（grouped=true 时使用） */
  const [groupDataList, setGroupDataList] =
    useState<GroupOption[]>(defaultGroupOptions);
  /** 分组数据备份（用于搜索还原） */
  const [groupDataCopy, setGroupDataCopy] =
    useState<GroupOption[]>(defaultGroupOptions);

  /**
   * 当前展开的分组 ID 数组
   * 格式: "__group_${groupKey}"
   *
   * 用途:
   * 1. 控制分组的展开/收起状态
   * 2. 分页加载时自动展开新分组
   * 3. 优化用户体验（不强制收起已展开的分组）
   */
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>(() =>
    defaultGroupOptions.map((g) => `__group_${g.groupKey}`),
  );

  /**
   * Effect: 分组数据变化时（新页加载），自动展开新分组
   *
   * 策略:
   * 1. 当加载新页面时，groupDataList 会包含新的分组
   * 2. 自动将这些新分组加入 expandedGroupIds
   * 3. 提升用户体验，用户无需手动展开分组查看数据
   *
   * 优化: 只在实际有新 ID 时才更新，避免不必要的重渲染
   */
  useEffect(() => {
    if (groupDataList.length) {
      setExpandedGroupIds((prev) => {
        const allGroupIds = groupDataList.map((g) => `__group_${g.groupKey}`);
        const merged = Array.from(new Set([...prev, ...allGroupIds]));
        // 只有实际有新 ID 时才返回新数组，避免不必要的重渲染
        if (merged.length === prev.length) return prev;
        return merged;
      });
    }
  }, [groupDataList]);

  /**
   * 计算：所有分组是否都已展开
   *
   * 用途: 在 scroll 事件中判断是否允许分页加载
   * 逻辑: 若某些分组未展开，则用户看不到该分组的数据，
   *      继续加载没有意义，所以只在全部展开时允许分页
   */
  const areAllGroupsExpanded = useMemo(() => {
    if (!groupDataList.length) return true;
    const allGroupIds = groupDataList.map((g) => `__group_${g.groupKey}`);
    return allGroupIds.every((id) => expandedGroupIds.includes(id));
  }, [groupDataList, expandedGroupIds]);

  // ============ Refs for Scroll Handler Stability ============

  /**
   * 使用 useRef 存储状态，保证 scroll 事件回调能获取最新值
   *
   * 背景: addEventListener 创建的回调是闭包，只能访问闭包时的值
   * 解决: 将状态存储在 ref 中，回调每次都读 ref.current 来获取最新值
   *
   * 以下 refs 都用同样的目的:
   * - areAllGroupsExpandedRef
   * - pageConfigRef
   * - dataListLenRef
   * - searchParamsRef
   */
  const areAllGroupsExpandedRef = useRef(areAllGroupsExpanded);
  areAllGroupsExpandedRef.current = areAllGroupsExpanded;
  const pageConfigRef = useRef(pageConfig);
  pageConfigRef.current = pageConfig;
  const dataListLenRef = useRef(
    grouped ? groupDataList.length : dataList.length,
  );
  dataListLenRef.current = grouped ? groupDataList.length : dataList.length;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // ============ Scroll Pagination Handler (Grouped Mode) ============

  /**
   * 分组模式的滚动分页处理
   *
   * 核心逻辑:
   * 1. 监听 Tree 外层滚动容器（分组视图）
   * 2. 当滚动到底部时（scrollHeight ≈ scrollTop + clientHeight）
   * 3. 检查是否满足分页条件：
   *    - 未在加载中（isLoadingRef.current === false）
   *    - 所有分组都已展开（areAllGroupsExpandedRef.current === true）
   *    - 还有更多数据（pageConfigRef.current.total > dataList 长度）
   * 4. 如果满足，则 pageIndex += 1，触发新一页的加载
   *
   * 注意:
   * - 使用 callback ref 来正确处理 listener 的生命周期
   * - 在组件卸载时清理 listener
   */
  const groupedScrollCleanup = useRef<(() => void) | null>(null);
  const groupedTreeOuterRef = useCallback((el: HTMLDivElement | null) => {
    // 清理旧的监听器
    if (groupedScrollCleanup.current) {
      groupedScrollCleanup.current();
      groupedScrollCleanup.current = null;
    }
    if (!el) return;
    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = el;
      // 判断是否滚动到底部（留 1px 误差容差）
      if (scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
        if (isLoadingRef.current) return;
        if (!areAllGroupsExpandedRef.current) return;
        if (
          pageConfigRef.current.total > dataListLenRef.current &&
          searchParamsRef.current.pageIndex
        ) {
          setSearchValue({ pageIndex: searchParamsRef.current.pageIndex + 1 });
          setIsSearch(true);
        }
      }
    };
    el.addEventListener('scroll', handleScroll);
    groupedScrollCleanup.current = () =>
      el.removeEventListener('scroll', handleScroll);
  }, []);

  // 定义一个外层容器的 Ref
  const containerRef = useRef<HTMLDivElement>(null);
  // 清理函数引用
  const scrollCleanupRef = useRef<(() => void) | null>(null);

  // 定义滚动事件处理函数
  const handleTreeScroll = (e: Event) => {
    const el = e.currentTarget as HTMLDivElement;
    const { scrollHeight, scrollTop, clientHeight } = el;

    // 判断是否滚动到底部（留 1px 误差容差，逻辑同原代码）
    if (scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
      // 同样的业务逻辑判断
      if (isLoadingRef.current) return;
      if (!areAllGroupsExpandedRef.current) return;

      if (
        pageConfigRef.current.total > dataListLenRef.current &&
        searchParamsRef.current.pageIndex
      ) {
        // 在触发异步流程前立即标记 loading，阻止下一次滚动事件触发请求
        isLoadingRef.current = true;
        setSearchValue({ pageIndex: searchParamsRef.current.pageIndex + 1 });
        setIsSearch(true);
      }
    }
  };

  useEffect(() => {
    // 2. 通过外层 containerRef 查找内部的滚动容器
    // 这种方式不需要依赖 Tdesign 内部的 ref 实现差异
    const container = containerRef.current;
    if (!container) return;

    // 查找 .t-tree__vscroll 节点
    const scrollContainer = container.querySelector(
      '.t-tree__vscroll',
    ) as HTMLDivElement;

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleTreeScroll);
      scrollCleanupRef.current = () => {
        scrollContainer.removeEventListener('scroll', handleTreeScroll);
      };
    }

    return () => {
      scrollCleanupRef.current?.();
    };
  }, [handleTreeScroll]);

  // ============ Scroll Pagination Handler (Flat Mode) ============

  /**
   * 扁平模式的滚动分页处理
   *
   * 与分组模式类似，但不需要检查分组展开状态
   * 用于普通列表数据的无限滚动
   */
  const flatScrollCleanup = useRef<(() => void) | null>(null);
  const flatTreeOuterRef = useCallback((el: HTMLDivElement | null) => {
    if (flatScrollCleanup.current) {
      flatScrollCleanup.current();
      flatScrollCleanup.current = null;
    }
    if (!el) return;
    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = el;
      if (scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
        if (isLoadingRef.current) return;
        if (
          pageConfigRef.current.total > dataListLenRef.current &&
          searchParamsRef.current.pageIndex
        ) {
          setSearchValue({ pageIndex: searchParamsRef.current.pageIndex + 1 });
          setIsSearch(true);
        }
      }
    };
    el.addEventListener('scroll', handleScroll);
    flatScrollCleanup.current = () =>
      el.removeEventListener('scroll', handleScroll);
  }, []);

  // ============ Virtual Scrolling Configuration ============

  /**
   * 虚拟滚动配置（分组模式）
   *
   * 关键设计点:
   * 1. VirtualizedOptions 包含外层滚动容器的 ref
   * 2. useMemo 保证对象引用不变，避免 Tree 组件不必要的重新创建虚拟滚动列表
   * 3. 如果每次数据变化都创建新的 VirtualizedOptions 对象，
   *    Tree 会重新初始化虚拟滚动，导致滚动位置重置
   *
   * 背景: Tea-component Tree 内部使用 useMemo([height, virtualizedOptions])
   *      如果 virtualizedOptions 引用变化，虚拟滚动列表会重新创建
   */
  const groupedVirtualizedOptions = useMemo(
    () => ({ outerRef: groupedTreeOuterRef } as any),
    [groupedTreeOuterRef],
  );

  /**
   * 虚拟滚动配置（扁平模式）
   * 与分组模式相同的设计原理
   */
  const flatVirtualizedOptions = useMemo(
    () => ({ outerRef: flatTreeOuterRef } as any),
    [flatTreeOuterRef],
  );

  // ============ Display Text Computation ============

  /**
   * 计算显示在按钮上的文字
   *
   * 逻辑:
   * 1. 如果使用自定义渲染，不显示文字
   * 2. 根据 grouped 模式获取对应的完整选项列表
   * 3. 根据值类型（单值/数组）匹配对应的 text
   * 4. 多个选项用逗号分隔
   *
   * 依赖: optsCopy, groupDataCopy, value（确保值变化时重算）
   */
  const selectText = useMemo(() => {
    if (customRender) return '';
    const allOpts = grouped
      ? [...pinnedOptions, ...flattenGroupOptions(groupDataCopy)]
      : optsCopy;
    let str: any = '';
    if ((allOpts.length && value && isNumber(value)) || isString(value))
      str = allOpts.find((i: any) => i.value === value)?.text;
    if (allOpts.length && value && isArray(value)) {
      str = allOpts
        .filter((i: any) => (value as any)?.includes(i.value))
        .map((i: any) => i?.text)
        .join();
    }
    return str;
  }, [optsCopy, groupDataCopy, value, customRender, grouped]);

  /**
   * 重置搜索和过滤条件
   * 用于清空搜索框时恢复初始状态
   */
  const onReset = useCallback(() => {
    setReqStatus(StatusTipEnum.init);
    searchKey &&
      setSearchValue({ [searchKey]: defaultSearchParams?.[searchKey] || '' });
  }, [staging, value, setDataList, setSearchValue]);

  // Render box
  const renderBox = useMemo(() => {
    const allOpts = grouped
      ? [...pinnedOptions, ...flattenGroupOptions(groupDataCopy)]
      : optsCopy;
    let dom: ReactNode = selectText;
    if (
      [DisplayTextEnum.CUSTOM_TEXT, DisplayTextEnum.SELECT_NUM].includes(
        renderType,
      )
    ) {
      dom = (
        <CustomSelect
          dataList={allOpts}
          renderType={renderType}
          selectValue={
            DisplayTextEnum.SELECT_NUM === renderType ? value : selectValue
          }
          placeholder={placeholder}
          defaultValue={defaultValue}
          onClosePop={onClosePop}
          customRender={customRender}
          resetSelectValue={resetSelectValue}
        />
      );
    }
    if (renderType == DisplayTextEnum.DRAG_SELECT) {
      dom =
        (checkValue(value) && staging) || (!staging && selectValue.length) ? (
          customRender?.({
            selectValue,
            list: allOpts,
          })
        ) : (
          <span style={{ color: '#8a949a' }}>{placeholder}</span>
        );
    }
    return dom;
  }, [
    optsCopy,
    groupDataCopy,
    renderType,
    selectText,
    placeholder,
    value,
    defaultValue,
    selectValue,
    customRender,
    grouped,
  ]);

  // Whether this is a "load more" (pagination) loading, not initial loading
  const isLoadingMore =
    reqStatus === StatusTipEnum.loading && searchParams.pageIndex > 1;

  // Bottom tips — only shown for initial load / empty / error states
  const bottomTips = useMemo(() => {
    // For pagination loading, don't replace the list — the list stays visible
    if (reqStatus === StatusTipEnum.loading && searchParams.pageIndex <= 1)
      return (
        <Loading
          size="small"
          loading
          text={`${intlGet('COMMON.LOADING')}...`}
        />
      );
    if (reqStatus === StatusTipEnum.empty)
      // return <EmptyTip emptyText={emptyText} />;
      return (
        <div className="ml-8 mr-8 font-size-12 color-bbb">
          {intlGet('COMMON.NO.DATA')}
        </div>
      );
    if (reqStatus === StatusTipEnum.error)
      return (
        <Link onClick={() => setIsSearch(true)}>{intlGet('COMMON.RETRY')}</Link>
      );
    return false;
  }, [reqStatus, renderType, searchParams.pageIndex]);

  // Open change callback
  const onOpenPop = useCallback(
    (openVal: boolean) => {
      setOpen(openVal);
      if (!openVal) {
        onReset();
        return;
      }
      if (openVal) {
        if (staging) setSelectValue(normalizeToArray(value));
        if (defaultSearchParams && !isEmptyObject(defaultSearchParams)) {
          setSearchParams((val: any) => {
            const obj = { ...val };
            Object.keys(defaultSearchParams).forEach((key) => {
              obj[key] = defaultSearchParams[key];
            });
            return obj;
          });
        }
      }
      if (getList) setIsSearch(true);
      if (!getList) {
        if (grouped) {
          setGroupDataList(defaultGroupOptions);
          const flatList = flattenGroupOptions(defaultGroupOptions);
          setDataList(flatList);
          flatList?.length
            ? setReqStatus(StatusTipEnum.success)
            : setReqStatus(StatusTipEnum.empty);
        } else {
          setDataList(defaultOptions);
          defaultOptions?.length
            ? setReqStatus(StatusTipEnum.success)
            : setReqStatus(StatusTipEnum.empty);
        }
      }
    },
    [
      defaultOptions,
      defaultGroupOptions,
      defaultSearchParams,
      searchParams,
      dataList,
      grouped,
    ],
  );

  // Is filtering
  const [isFilter, setIsFilter] = useState(false);
  const compareAndContactList = ({
    list,
    remberDataList = [],
  }: {
    list: any[];
    remberDataList?: any[];
  }) => {
    const resultList = [...remberDataList, ...list];

    if (!defaultOpts?.length || isFilter) return resultList;

    for (let i = 0; i < defaultOpts.length; i++) {
      const item = defaultOpts[i];
      const remberIndex = remberDataList.findIndex(
        (v) => v.value === item.value,
      );
      const listIndex = list.findIndex((v) => v.value === item.value);

      if (remberIndex === -1 && listIndex !== -1) continue;
      if (remberIndex !== -1 && listIndex === -1) continue;
      if ([remberIndex, listIndex].every((v) => v !== -1)) {
        resultList.splice(remberIndex, 1);
      } else {
        resultList.push(item);
      }
    }

    return resultList;
  };

  // Merge group data for pagination
  const mergeGroupData = (
    existingGroups: GroupOption[],
    newGroups: GroupOption[],
  ): GroupOption[] => {
    const merged = [...existingGroups];
    newGroups.forEach((newGroup) => {
      const existingIndex = merged.findIndex(
        (g) => g.groupKey === newGroup.groupKey,
      );
      if (existingIndex !== -1) {
        // Merge children, avoid duplicates
        const existingValues = new Set(
          merged[existingIndex].children.map((c) => c.value),
        );
        const uniqueNewChildren = newGroup.children.filter(
          (c) => !existingValues.has(c.value),
        );
        merged[existingIndex] = {
          ...merged[existingIndex],
          children: [...merged[existingIndex].children, ...uniqueNewChildren],
        };
      } else {
        merged.push(newGroup);
      }
    });
    return merged;
  };

  // Get data list
  const getDataList = useCallback(async () => {
    /** 列表接口响应（兼容 dataKey 嵌套与扁平两种结构） */
    type ListApiResponse = {
      data?: {
        total?: number;
        [key: string]: any;
      };
    };

    const newParams: Record<string, any> = { ...searchParams };
    isLoadingRef.current = true;
    setReqStatus(StatusTipEnum.loading);
    try {
      // 兼容 getList 缺省的情况，避免 ?.finally 在返回 undefined 时报错
      const result: ListApiResponse | undefined = getList
        ? await getList(newParams)
        : undefined;
      handleReturnResults({
        params: result,
        successCallBack: (res) => {
          const { [listKey]: data_list = [], total: rawTotal } = dataKey
            ? res?.data[dataKey]
            : res?.data || {};
          // 允许业务方通过 getTotal 自定义从响应体里提取总条数，
          // 兜底后端未将 total 平铺到约定位置的情况，避免滚动分页失效
          const customTotal = getTotal?.(res);
          const total = customTotal ?? rawTotal;
          const pageInfo = {
            total,
          };
          const isAllList: boolean =
            !newParams.pageIndex && !newParams[searchKey];

          if (grouped && getFilterGroupOptions) {
            // Group mode
            const groupList = getFilterGroupOptions(
              data_list || [],
              { ...newParams, totalPage: total },
              isAllList,
            );
            setPageConfig(pageInfo);

            if (newParams.pageIndex > 1) {
              const mergedGroups = mergeGroupData(groupDataList, groupList);
              // 使用函数式更新，确保拿到最新状态，避免闭包陷阱
              setGroupDataList((prev) => mergeGroupData(prev, groupList));
              setGroupDataCopy((prev) => mergeGroupData(prev, groupList));
              const flatList = flattenGroupOptions(mergedGroups);
              setDataList(flatList);
              setOptsCopy((val: any) => [
                ...val,
                ...flattenGroupOptions(groupList),
              ]);
            } else {
              setGroupDataList(groupList);
              setGroupDataCopy((val) => mergeGroupData(val, groupList));
              const flatList = flattenGroupOptions(groupList);
              setDataList(flatList);
              setOptsCopy((val: any) => [...val, ...flatList]);
            }
            const hasItems = groupList.some((g) => g.children?.length > 0);
            setReqStatus(
              hasItems ? StatusTipEnum.success : StatusTipEnum.empty,
            );
          } else {
            // Non-group mode (same as original)
            const list =
              getFilterOptions?.(
                data_list || [],
                { ...newParams, totalPage: total },
                isAllList,
              ) || [];

            setPageConfig(pageInfo);
            let newList = compareAndContactList({ list });

            if (newParams.pageIndex > 1) {
              newList = compareAndContactList({
                list,
                remberDataList: dataList,
              });
              setDataList(newList);
              setOptsCopy((val: any) => [...val, ...newList]);
            } else {
              setDataList(newList);
              setOptsCopy((val: any) => [...val, ...newList]);
            }
            setReqStatus(
              list?.length ? StatusTipEnum.success : StatusTipEnum.empty,
            );
          }
        },
        errorCallBack: () => setReqStatus(StatusTipEnum.error),
      });
    } catch (error) {
      setReqStatus(StatusTipEnum.error);
    } finally {
      // 统一在 finally 中重置 loading / search 标记，避免与回调竞态导致状态不一致
      isLoadingRef.current = false;
      setIsSearch(false);
    }
  }, [
    searchParams,
    reqStatus,
    listKey,
    dataKey,
    defaultSearchParams,
    optsCopy,
    getFilterOptions,
    getFilterGroupOptions,
    setIsSearch,
    getList,
    grouped,
    groupDataList,
  ]);

  // Get all selected values
  const getAllSelected = (list: any, val?: any[]): any[] => {
    const values: any[] = val || [];
    let children: any[] = [];
    if (!list?.length) return values;
    list.map((i: any) => {
      i.value && values.push(i.value);
      if (i?.children?.length) children = [...children, ...i.children];
    });
    if (children.length) return getAllSelected(children, values);
    return values;
  };

  // Get all selected values from groups
  const getAllGroupSelected = (groups: GroupOption[]): any[] => {
    return groups.reduce<any[]>((acc, group) => {
      return [...acc, ...group.children.map((c) => c.value)];
    }, []);
  };

  // Select all logic
  const onSelectAll = ({
    selectedIds,
    isAllNode,
    allStatus,
    newList,
  }: {
    selectedIds: any[];
    isAllNode: boolean;
    allStatus: boolean;
    newList: any[];
  }) => {
    let newSelectedIds = [...selectedIds];

    if (isAllNode && allStatus) {
      newSelectedIds = grouped
        ? getAllGroupSelected(groupDataList)
        : getAllSelected(newList);
    }
    if (isAllNode && !allStatus) newSelectedIds = [];

    const allOptionsValue = grouped
      ? getAllGroupSelected(groupDataList)
      : getAllSelected(dataList);
    const other = selectedIds.filter((i) => i !== ALL);
    if (other.length === allOptionsValue.length && !isAllNode)
      newSelectedIds = [ALL, ...other];
    if (other.length !== allOptionsValue.length && !isAllNode)
      newSelectedIds = newSelectedIds.filter((i) => i !== ALL);
    return newSelectedIds;
  };

  // 在组件顶层定义 treeData 的构建逻辑
  const treeData = useMemo(() => {
    if (!grouped) return []; // 非 grouped 模式不构建

    const list: any[] = [];
    const hasAll = allOption && !searchParams[searchKey];

    // All Option
    if (hasAll) {
      list.push({
        ...allOption,
        content: (
          <Checkbox
            name={ALL}
            display="block"
            indeterminate={
              isArray(value) &&
              !(value as any)?.includes(ALL) &&
              (value as any)?.length
            }
          >
            {allOption.text}
          </Checkbox>
        ),
      });
    }

    // Pinned options (top-level, outside any group)
    pinnedOptions.forEach((item: any) => {
      list.push({
        ...item,
        id: item.id || item.value,
        content: item.content || item.text,
        label: item.content || item.text,
      });
    });

    groupDataList.forEach((group) => {
      // Add group header as a non-selectable parent node
      list.push({
        id: `__group_${group.groupKey}`,
        value: `__group_${group.groupKey}`,
        text: group.groupLabel,
        label: group.groupLabel,
        content: group.groupLabel,
        selectable: false,
        children: group.children.map((child: any) => ({
          ...child,
          id: child.id || child.value,
          content: child.content || child.text,
          label: child.content || child.text,
        })),
      });
    });

    return list;
  }, [groupDataList, pinnedOptions, allOption, searchParams, value]);

  // Render grouped list for CUSTOM_TEXT mode (Tree)
  const renderGroupedTree = useCallback(
    (close: () => void) => {
      const hasAll = allOption && !searchParams[searchKey];
      return (
        <div ref={containerRef}>
          <CustomTree
            data={treeData}
            defaultValue={selectValue}
            onChange={(selectedIds, contextData) => {
              const context = {
                ...contextData.node,
                nodeId: contextData.node.value,
                selected: !!selectedIds.filter(
                  (item) => !selectValue.includes(item),
                )?.length,
              };

              // Filter out group header ids
              const filteredIds = selectedIds.filter(
                (id) => !String(id).startsWith('__group_'),
              );

              let newSelectedIds: any[];

              if (!isMultiple) {
                // Single select mode: only keep the newly selected item
                if (context.selected) {
                  newSelectedIds = [context.nodeId];
                } else {
                  // Deselect: clear selection
                  newSelectedIds = [];
                }
              } else {
                // Multiple select mode (original logic)
                newSelectedIds = !searchParams[searchKey]
                  ? [...filteredIds]
                  : Array.from(new Set([...selectValue, ...filteredIds]));

                if (searchParams[searchKey]) {
                  const cancelIndex = newSelectedIds.findIndex(
                    (item) => !context.selected && item === context.nodeId,
                  );
                  if (cancelIndex !== -1) newSelectedIds.splice(cancelIndex, 1);
                }

                if (hasAll) {
                  newSelectedIds = onSelectAll({
                    selectedIds: filteredIds,
                    isAllNode: context.nodeId === ALL,
                    allStatus: context.selected,
                    newList: treeData,
                  });
                }
              }
              setSelectValue(newSelectedIds);

              // 从当前渲染的 treeData 中提取所有叶节点（保留 _raw 等自定义字段）
              const allTreeLeaves = treeData.flatMap((node: any) =>
                node.children
                  ? node.children
                  : node.selectable !== false
                  ? [node]
                  : [],
              );
              const selectedOptions = allTreeLeaves.filter((opt: any) =>
                newSelectedIds.includes(opt.id ?? opt.value),
              );

              // For single select, pass string value instead of array
              const outputValue = !isMultiple
                ? newSelectedIds[0] || ''
                : newSelectedIds;
              onChange(outputValue, {
                option: context,
                selectedOptions,
                list: allTreeLeaves,
              });

              // Single select: auto close dropdown after selection
              if (!isMultiple && context.selected) {
                close();
              }
            }}
            expanded={expandedGroupIds}
            onExpand={(expandedIds: string[]) => {
              setExpandedGroupIds(expandedIds);
            }}
            onScroll={handleTreeScroll}
          />
        </div>
      );
    },
    [
      value,
      selectValue,
      groupDataList,
      groupDataCopy,
      dataList,
      open,
      allOption,
      pinnedOptions,
      searchParams,
      onChange,
      pageConfig,
      reqStatus,
      expandedGroupIds,
      treeData,
    ],
  );

  // Render grouped list for DRAG_SELECT / SELECT_NUM mode (Checkbox.Group)
  const renderGroupedCheckboxList = useCallback(
    (close: () => void) => {
      const hasAll = allOption && !searchParams[searchKey];

      return (
        <List type="option" className="multiple-list">
          <Checkbox.Group
            defaultValue={value && (value as any)}
            value={
              DisplayTextEnum.DRAG_SELECT == renderType
                ? selectValue
                : value && (value as any)
            }
            onChange={(
              selectVal: any[],
              context: CheckboxGroupChangeContext,
            ) => {
              const { current: check } = context;
              let newSelectedIds: any[];

              if (!isMultiple) {
                // Single select mode: only keep the newly checked item
                newSelectedIds = check.value ? [check.name] : [];
              } else {
                // Multiple select mode (original logic)
                newSelectedIds = selectVal;
                if (allOption) {
                  newSelectedIds = onSelectAll({
                    selectedIds: selectVal,
                    isAllNode: check.name === ALL,
                    allStatus: !!check.value,
                    newList: flattenGroupOptions(groupDataList),
                  });
                }
              }

              const allOpts = [
                ...pinnedOptions,
                ...flattenGroupOptions(groupDataList),
              ];
              const selectedOptions = allOpts.filter((item: optsTypes) =>
                newSelectedIds.includes(item.value),
              );
              setSelectValue(newSelectedIds);

              const outputValue = !isMultiple
                ? newSelectedIds[0] || ''
                : newSelectedIds;
              onChange?.(outputValue, {
                option: context,
                selectedOptions,
                list: allOpts,
              });

              // Single select: auto close dropdown
              if (!isMultiple && check.value) {
                close();
              }
            }}
          >
            {/* All option */}
            {hasAll && (
              <List.Item
                title={allOption.text}
                className="pl-10 pr-10 pt-6 pb-6"
              >
                <Checkbox
                  name={ALL}
                  className="width-100 mb-0"
                  display="block"
                  indeterminate={
                    isArray(value) &&
                    !(value as any)?.includes(ALL) &&
                    (value as any)?.length
                  }
                >
                  {allOption.text}
                </Checkbox>
              </List.Item>
            )}
            {/* Pinned options (top-level, outside any group) */}
            {pinnedOptions.map((i: optsTypes, index: number) => (
              <List.Item
                key={`pinned_${index}`}
                title={i.text}
                className="pl-10 pr-10 pt-6 pb-6"
              >
                <Checkbox
                  name={i.value}
                  disabled={i.disabled}
                  className="width-100 mb-0"
                  display="block"
                >
                  {i.text}
                </Checkbox>
              </List.Item>
            ))}
            {/* Grouped items */}
            {groupDataList.map((group) => (
              <div key={group.groupKey} className="select-group">
                <div
                  className="select-group-header"
                  style={{
                    padding: '6px 10px',
                    fontWeight: 600,
                    color: '#888',
                    fontSize: '12px',
                    borderBottom: '1px solid #eee',
                    userSelect: 'none',
                  }}
                >
                  {group.groupLabel}
                </div>
                {group.children.map((i: optsTypes, index: number) => (
                  <List.Item
                    key={`${group.groupKey}_${index}`}
                    title={i.text}
                    className="pl-10 pr-10 pt-6 pb-6"
                  >
                    <Checkbox
                      name={i.value}
                      disabled={i.disabled}
                      className="width-100 mb-0"
                      display="block"
                    >
                      {i.text}
                    </Checkbox>
                  </List.Item>
                ))}
              </div>
            ))}
          </Checkbox.Group>
        </List>
      );
    },
    [
      value,
      selectValue,
      renderType,
      groupDataList,
      open,
      allOption,
      pinnedOptions,
      searchParams,
      onChange,
    ],
  );

  // Scroll list content (non-grouped, same as original)
  const scrollList = useCallback(
    (close: () => void) => {
      // If grouped mode, delegate to group renderers
      if (grouped) {
        if (renderType === DisplayTextEnum.CUSTOM_TEXT) {
          return renderGroupedTree(close);
        }
        if (
          [DisplayTextEnum.DRAG_SELECT, DisplayTextEnum.SELECT_NUM].includes(
            renderType,
          )
        ) {
          return renderGroupedCheckboxList(close);
        }
        return null;
      }

      // Non-grouped mode (original logic)
      let newDataList = dataList;
      const hasAll = allOption && !searchParams[searchKey];
      if (hasAll)
        newDataList = [
          {
            ...allOption,
            content: (
              <Checkbox
                name={ALL}
                display="block"
                indeterminate={
                  isArray(value) &&
                  !(value as any)?.includes(ALL) &&
                  (value as any)?.length
                }
              >
                {allOption.text}
              </Checkbox>
            ),
          },
          ...dataList,
        ];

      setListDisable && (newDataList = setListDisable?.(dataList, selectValue));
      if (renderType === DisplayTextEnum.CUSTOM_TEXT) {
        return (
          <Tree
            data={newDataList}
            selectable
            className={classNames('tree-list', { 'tree-all-list': hasAll })}
            defaultExpandAll={true}
            selectedIds={selectValue}
            onSelect={(selectedIds: any[], context: any) => {
              let newSelectedIds: any[];

              if (!isMultiple) {
                // Single select mode
                if (context.selected) {
                  newSelectedIds = [context.nodeId];
                } else {
                  newSelectedIds = [];
                }
              } else {
                // Multiple select mode (original logic)
                newSelectedIds = !searchParams[searchKey]
                  ? [...selectedIds]
                  : Array.from(new Set([...selectValue, ...selectedIds]));

                if (searchParams[searchKey]) {
                  const cancelIndex = newSelectedIds.findIndex(
                    (item) => !context.selected && item === context.nodeId,
                  );
                  if (cancelIndex !== -1) newSelectedIds.splice(cancelIndex, 1);
                }

                if (hasAll) {
                  newSelectedIds = onSelectAll({
                    selectedIds,
                    isAllNode: context.nodeId === ALL,
                    allStatus: context.selected,
                    newList: newDataList,
                  });
                }
              }
              setSelectValue(newSelectedIds);

              const selectedOptions =
                selectTreeChildren({
                  selectValue: newSelectedIds,
                  dataList: optsCopy,
                })?.selectChildList || [];

              const outputValue = !isMultiple
                ? newSelectedIds[0] || ''
                : newSelectedIds;
              onChange(outputValue, {
                option: context,
                selectedOptions,
                list: optsCopy,
              });

              // Single select: auto close dropdown
              if (!isMultiple && context.selected) {
                close();
              }
            }}
            filterState={!!value}
            defaultExpandedIds={[]}
            height={[30, 200]}
            virtualizedOptions={flatVirtualizedOptions}
          />
        );
      }

      if (
        [DisplayTextEnum.DRAG_SELECT, DisplayTextEnum.SELECT_NUM].includes(
          renderType,
        )
      ) {
        return (
          <List type="option" className="multiple-list">
            <Checkbox.Group
              defaultValue={value && (value as any)}
              value={
                DisplayTextEnum.DRAG_SELECT == renderType
                  ? selectValue
                  : value && (value as any)
              }
              onChange={(
                selectVal: any[],
                context: CheckboxGroupChangeContext,
              ) => {
                const { check } = context;
                let newSelectedIds: any[];

                if (!isMultiple) {
                  // Single select mode
                  newSelectedIds = check.value ? [check.name] : [];
                } else {
                  // Multiple select mode (original logic)
                  newSelectedIds = selectVal;
                  if (allOption) {
                    newSelectedIds = onSelectAll({
                      selectedIds: selectVal,
                      isAllNode: check.name === ALL,
                      allStatus: !!check.value,
                      newList: newDataList,
                    });
                  }
                }

                const selectedOptions = dataList.filter((item: optsTypes) =>
                  newSelectedIds.includes(item.value),
                );
                setSelectValue(newSelectedIds);

                const outputValue = !isMultiple
                  ? newSelectedIds[0] || ''
                  : newSelectedIds;
                onChange?.(outputValue, {
                  option: context,
                  selectedOptions,
                  list: dataList,
                });

                // Single select: auto close dropdown
                if (!isMultiple && check.value) {
                  close();
                }
              }}
            >
              {newDataList.map((i: optsTypes, index: number) => (
                <List.Item
                  key={index}
                  title={i.text}
                  className="pl-10 pr-10 pt-6 pb-6"
                >
                  <Checkbox
                    name={i.value}
                    disabled={i.disabled}
                    className="width-100 mb-0"
                    display="block"
                    indeterminate={
                      i.value === ALL &&
                      isArray(value) &&
                      !(value as any)?.includes(ALL) &&
                      (value as any)?.length
                    }
                  >
                    {i.text}
                  </Checkbox>
                </List.Item>
              ))}
            </Checkbox.Group>
          </List>
        );
      }
    },
    [
      value,
      selectValue,
      renderType,
      dataList,
      open,
      allOption,
      searchParams,
      optsCopy,
      onChange,
      grouped,
      groupDataList,
      groupDataCopy,
      renderGroupedTree,
      renderGroupedCheckboxList,
      pageConfig,
      reqStatus,
    ],
  );

  // Search
  const searchAction = (searchValue: any) => {
    let params: any = {
      pageIndex: defaultSearchParams?.pageIndex === 0 ? 0 : 1,
    };
    if (searchKey) {
      params = {
        ...params,
        [searchKey]: searchValue,
      };
    }
    setSearchValue(params);
    setIsFilter(true);
    if (onSearch) {
      setDataList([]);
      if (grouped) setGroupDataList([]);
      onSearch(searchValue);
      return;
    }
    // Non-API search
    if (!getList) {
      setDataList([]);
      if (grouped) {
        // Search within groups
        if (searchValue) {
          const filteredGroups = groupDataCopy
            .map((group) => ({
              ...group,
              children: group.children.filter((i: any) =>
                i.text
                  ?.toLocaleLowerCase()
                  ?.includes(searchValue?.toLocaleLowerCase()),
              ),
            }))
            .filter((group) => group.children.length > 0);
          setGroupDataList(filteredGroups);
          const flatList = flattenGroupOptions(filteredGroups);
          setDataList(flatList);
          flatList.length
            ? setReqStatus(StatusTipEnum.success)
            : setReqStatus(StatusTipEnum.empty);
        } else {
          setGroupDataList([...groupDataCopy]);
          const flatList = flattenGroupOptions(groupDataCopy);
          setDataList(flatList);
          flatList.length
            ? setReqStatus(StatusTipEnum.success)
            : setReqStatus(StatusTipEnum.empty);
        }
        return;
      }
      if (searchValue) {
        const newList = optsCopy.filter((i: any) =>
          i.text
            ?.toLocaleLowerCase()
            ?.includes(searchValue?.toLocaleLowerCase()),
        );
        setDataList(newList);
        newList.length
          ? setReqStatus(StatusTipEnum.success)
          : setReqStatus(StatusTipEnum.empty);
      } else {
        setDataList([...optsCopy]);
        optsCopy.length
          ? setReqStatus(StatusTipEnum.success)
          : setReqStatus(StatusTipEnum.empty);
      }
      return;
    }
    setDataList([]);
    if (grouped) setGroupDataList([]);
    setIsSearch(true);
    throttle(() => {
      setIsSearch(false);
    }, 4)();
  };

  // Trigger get list
  useEffect(() => {
    if (isSearch && getDataList) getDataList();
  }, [isSearch]);

  useImperativeHandle(refs, () => ({
    setSelectValue,
  }));

  useEffect(() => {
    if (!getList) {
      setOptsCopy(fullBackupList || defaultOptions);
      if (grouped) {
        setGroupDataCopy(defaultGroupOptions);
      }
    }
  }, [getList, fullBackupList, defaultOptions, grouped, defaultGroupOptions]);

  useEffect(() => {
    if (!open) {
      setSearchParams({
        pageIndex: 1,
        pageSize: 10,
        ...defaultSearchParams,
      });
    }
  }, [open, defaultSearchParams]);

  useEffect(() => {
    if (open) {
      onOpenPop(open);
    }
  }, [open]);
  return (
    <Popup
      // options={downloadMenu}
      visible={open}
      trigger="click"
      placement="bottom-left"
      destroyOnClose
      overlayInnerStyle={{
        ...overlayInnerStyle,
      }}
      onVisibleChange={setOpen}
      // disabled={!hasClickDataCurLang || isDownloading}
      overlayClassName="paging-select-group-popup"
      content={
        <div className="pop-content">
          {/* Search */}
          {searchable && (
            <SearchBox
              // simple
              // autoFocus={true}
              // size=''
              // iconPlacement="end"
              size={InputSizeEnum.FULL}
              value={searchParams[searchKey]}
              // defaultValue={searchParams[searchKey] || ''}
              className="search-box"
              placeholder={searchPlaceHolder}
              onSearch={searchAction}
              onChange={searchAction}
            />
          )}
          {/* Keep the list always mounted so scroll position is preserved across
              loading states. Only hide it when showing full-page status tips
              (initial loading / empty / error). */}
          {(() => {
            const showFullPageTip =
              !isLoadingMore &&
              (
                [
                  StatusTipEnum.loading,
                  StatusTipEnum.empty,
                  StatusTipEnum.error,
                ] as any
              ).includes(reqStatus);

            // 有 pinnedOptions 且为空数据状态时，只渲染 pinnedOptions，不显示"暂无数据"
            if (
              showFullPageTip &&
              pinnedOptions.length > 0 &&
              reqStatus === StatusTipEnum.empty
            ) {
              return scrollList(onClosePop);
            }

            if (showFullPageTip) return bottomTips;

            return (
              <>
                {scrollList(onClosePop)}
                {isLoadingMore && (
                  <Loading
                    size="small"
                    loading
                    text={`${intlGet('COMMON.LOADING')}...`}
                  />
                )}
              </>
            );
          })()}
        </div>
      }
    >
      <div>{renderBox}</div>
    </Popup>
  );
};

export default forwardRef(PagingSelectGroup);
